import { MouseEventHandler } from 'react'

import clsx from 'clsx'

import { ChildProps } from '@/common/util/props'

import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import ExternalLink from '@/components/view/utils/link/ExternalLink'
import InternalLink from '@/components/view/utils/link/InternalLink'

export const ButtonColor = {
  PRIMARY: 'primary',
  PRIMARY_BRAND: 'primary-brand',
  CRITICAL: 'critical',
  SECONDARY: 'secondary',
  TERTIARY: 'tertiary'
} as const

export type ButtonColor = (typeof ButtonColor)[keyof typeof ButtonColor]

interface Props extends ChildProps {
  type?: 'submit' | 'reset' | 'button'
  color?: ButtonColor
  size?: 'base' | 'large' | 'full' | 'icon'
  link?: string
  disabled?: boolean
  loading?: boolean
  inversion?: boolean
  newTab?: boolean
  square?: boolean
  shrink0?: boolean
  onClick?: MouseEventHandler<HTMLButtonElement>
  form?: string
}

const Button = ({
  children,
  type = 'button',
  color = 'primary',
  size = 'base',
  link,
  disabled = false,
  loading = false,
  inversion = false,
  newTab = false,
  square = false,
  shrink0 = false,
  onClick,
  form
}: Props) => {
  const isDisabledColor = loading || disabled

  const buttonColor = inversion
    ? isDisabledColor
      ? 'bg-fill-disabled-on-bg-fill'
      : color == 'primary'
        ? 'bg-surface'
        : color == 'tertiary'
          ? 'bg-fill-brand-on-bg-fill'
          : ''
    : isDisabledColor
      ? 'bg-fill-disabled'
      : color == 'primary'
        ? 'bg-fill-brand'
        : color == 'primary-brand'
          ? 'bg-fill-primary-brand'
          : color == 'critical'
            ? 'bg-fill-critical'
            : color == 'secondary'
              ? 'bg-fill-primary'
              : 'bg-fill-tertiary'

  const buttonHoverColor = isDisabledColor
    ? ''
    : inversion
      ? color == 'primary'
        ? 'hover:bg-fill-hover'
        : color == 'primary-brand'
          ? 'hover:bg-fill-primary-brand-hover'
          : color == 'tertiary'
            ? 'hover:bg-fill-brand-on-bg-fill-hover'
            : ''
      : color == 'primary'
        ? 'hover:bg-fill-brand-hover'
        : color == 'primary-brand'
          ? 'hover:bg-fill-primary-brand-hover'
          : color == 'critical'
            ? 'hover:bg-fill-critical-hover'
            : color == 'secondary'
              ? 'hover:bg-fill-hover'
              : 'hover:bg-fill-tertiary-hover'

  const buttonActiveColor = isDisabledColor
    ? ''
    : inversion
      ? color == 'primary'
        ? 'active:bg-surface-primary-active'
        : color == 'primary-brand'
          ? 'active:bg-fill-primary-brand-active'
          : color == 'tertiary'
            ? 'active:bg-fill-brand-on-bg-fill-active'
            : 'active:bg-fill-disabled-secondary-active'
      : color == 'primary'
        ? 'active:bg-fill-brand-active'
        : color == 'primary-brand'
          ? 'active:bg-fill-primary-brand-active'
          : color == 'critical'
            ? 'active:bg-fill-critical-active'
            : color == 'secondary'
              ? 'active:bg-fill-active'
              : 'active:bg-fill-tertiary-active'

  const buttonShadow = isDisabledColor
    ? 'shadow-none'
    : inversion
      ? color == 'primary'
        ? 'shadow-button'
        : color == 'tertiary'
          ? 'shadow-none'
          : 'shadow-none'
      : color == 'primary' || color == 'critical'
        ? 'shadow-button-primary'
        : color == 'secondary'
          ? 'shadow-button'
          : 'shadow-none'

  const buttonHoverShadow =
    color == 'primary' || color == 'critical'
      ? 'hover:shadow-button-primary-hover'
      : color == 'secondary'
        ? 'hover:shadow-button-hover'
        : 'shadow-none'

  const buttonActiveShadow = inversion
    ? color == 'primary'
      ? 'active:shadow-button-inset'
      : 'shadow-none'
    : color == 'primary' || color == 'primary-brand' || color == 'critical'
      ? 'active:shadow-button-primary-inset'
      : color == 'secondary'
        ? 'active:shadow-button-inset'
        : 'shadow-none'

  const textColor = isDisabledColor
    ? 'text-disabled'
    : inversion
      ? color == 'primary' || color == 'primary-brand' || color == 'critical'
        ? 'text-primary'
        : 'text-on-bg-fill'
      : color == 'primary' || color == 'primary-brand' || color == 'critical'
        ? 'text-on-bg-fill'
        : 'text-primary'

  const buttonSize =
    size == 'base'
      ? 'text-size-12 h-6.5'
      : size == 'large'
        ? 'text-size-13 h-8'
        : size == 'icon'
          ? 'p-2'
          : 'text-size-14 p-3 w-full h-[41px]'

  const iconColor = inversion
    ? color == 'primary'
      ? IconColor.PRIMARY
      : IconColor.ON_COLOR
    : color == 'secondary' || color == 'tertiary'
      ? IconColor.PRIMARY
      : IconColor.ON_COLOR

  const iconHoverColor = inversion
    ? color == 'primary'
      ? IconColor.SECONDARY_HOVER
      : IconColor.ON_COLOR
    : color == 'secondary' || color == 'tertiary'
      ? IconColor.SECONDARY_HOVER
      : IconColor.ON_COLOR

  const iconActiveColor = inversion
    ? color == 'primary'
      ? IconColor.SECONDARY_ACTIVE
      : IconColor.ON_COLOR
    : color == 'secondary' || color == 'tertiary'
      ? IconColor.SECONDARY_ACTIVE
      : IconColor.ON_COLOR

  const Tag = newTab ? ExternalLink : InternalLink
  const className = clsx(
    `${buttonHoverColor} ${buttonActiveColor} ${buttonSize} ${buttonColor} ${buttonShadow} ${buttonHoverShadow} ${buttonActiveShadow}`,
    'inline-flex cursor-pointer items-center justify-center rounded-lg leading-100 w-fit whitespace-nowrap min-w-[24px]',
    size != 'icon' && (square ? 'px-[5px] aspect-square' : 'px-3'),
    !disabled && 'outline-default',
    loading && ' min-w-[76px]',
    shrink0 && 'shrink-0'
  )

  return (
    <>
      {link ? (
        <Tag
          link={link}
          className={clsx(className, disabled ? 'text-disabled pointer-events-none' : textColor)}
          tabIndex={disabled ? -1 : 0}
        >
          {children}
          {newTab && (
            <Icon
              color={iconColor}
              hoverColor={iconHoverColor}
              activeColor={iconActiveColor}
              type={IconType.NEW_TAB}
            />
          )}
        </Tag>
      ) : (
        <button
          type={type}
          className={clsx(className, textColor, `disabled:pointer-events-none`)}
          onClick={onClick}
          disabled={disabled || loading}
          form={form}
        >
          {loading ? (
            <Icon color={IconColor.SECONDARY} type={IconType.LOADING} spin />
          ) : (
            <>
              {children}
              {newTab && (
                <Icon
                  color={iconColor}
                  hoverColor={iconHoverColor}
                  activeColor={iconActiveColor}
                  type={IconType.NEW_TAB}
                />
              )}
            </>
          )}
        </button>
      )}
    </>
  )
}

export default Button
